import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Chip,
  OutlinedInput,
  ListItemText,
  TableCell,
  TableRow,
  Paper,
  Grid,
  Tooltip,
  CircularProgress,
  Divider,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../config/api';
import DataTable from '../components/DataTable';
import ClockTimeSlotPicker from '../components/ClockTimeSlotPicker';
import * as XLSX from 'xlsx';
import { getAreasForDistrict } from '../utils/districtAreas';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    source_website: '',
    state_id: '',
    district_id: '',
    city_id: '',
    area: '',
    pincode: '',
    start_plan: '',
    available_days: '',
    service_type: 'home_services',
    category_id: '',
    subcategory_ids: [],
  });
  const [error, setError] = useState('');
  
  // Filter states
  const [filterState, setFilterState] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterJoinedMonth, setFilterJoinedMonth] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewSessions, setViewSessions] = useState([]);

  // Assign trainer & per-session schedule state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserForAssign, setSelectedUserForAssign] = useState(null);
  const [trainersList, setTrainersList] = useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [trainerSelectMode, setTrainerSelectMode] = useState('phone'); // 'phone' or 'name'
  const [activePackage, setActivePackage] = useState('demo'); // 'demo', 'package_a', 'package_b', 'custom'
  const [numberOfSessions, setNumberOfSessions] = useState(1);
  const [sessionsList, setSessionsList] = useState([]);
  const [scheduleStartDate, setScheduleStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [scheduleDefaultTime, setScheduleDefaultTime] = useState('07:00 AM – 08:00 AM');
  const [scheduleFrequency, setScheduleFrequency] = useState('package_a'); // 'package_a', 'package_b', 'alternate', 'daily', 'weekdays'
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');

  // Clock-like time picker state
  const [clockPickerOpen, setClockPickerOpen] = useState(false);
  const [clockPickerTarget, setClockPickerTarget] = useState(null); // 'batch' or session index number
  const [clockPickerValue, setClockPickerValue] = useState('07:00 AM – 08:00 AM');

  const handleOpenClockPicker = (target) => {
    setClockPickerTarget(target);
    if (target === 'batch') {
      setClockPickerValue(scheduleDefaultTime || '07:00 AM – 08:00 AM');
    } else if (typeof target === 'number' && sessionsList[target]) {
      setClockPickerValue(sessionsList[target].session_time || scheduleDefaultTime || '07:00 AM – 08:00 AM');
    }
    setClockPickerOpen(true);
  };

  const handleClockTimeSelect = (selectedSlot) => {
    if (clockPickerTarget === 'batch') {
      setScheduleDefaultTime(selectedSlot);
      // Synchronize all existing sessions in the list to the selected slot
      setSessionsList((prev) =>
        prev.map((s) => ({
          ...s,
          session_time: selectedSlot,
        }))
      );
    } else if (typeof clockPickerTarget === 'number') {
      handleSessionChange(clockPickerTarget, 'session_time', selectedSlot);
    }
  };

  const selectedTrainerObj = useMemo(() => {
    if (!selectedTrainerId) return null;
    return trainersList.find((t) => String(t.id) === String(selectedTrainerId)) || null;
  }, [trainersList, selectedTrainerId]);

  // Safe date helpers avoiding timezone skew
  const parseDateSafe = (str) => {
    if (!str) return new Date();
    if (typeof str === 'string' && str.includes('-')) {
      const parts = str.split('-').map(Number);
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
      }
    }
    const d = new Date(str);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const formatDateSafe = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDayLabel = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = parseDateSafe(dateStr);
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    } catch (e) {
      return '';
    }
  };

  // Helper to generate sequential dates & times strictly matching package patterns
  // Package A: Monday (1), Wednesday (3), Friday (5)
  // Package B: Tuesday (2), Thursday (4), Saturday (6)
  const generateSessions = (count, startDateStr, defaultTime, frequency) => {
    const total = Math.max(1, parseInt(count, 10) || 1);
    const result = [];
    let current = parseDateSafe(startDateStr);

    let attempts = 0;
    while (result.length < total && attempts < 1000) {
      attempts++;
      const day = current.getDay(); // 0 is Sun, 1 is Mon, 2 is Tue, 3 is Wed, 4 is Thu, 5 is Fri, 6 is Sat
      let isValid = false;

      if (frequency === 'package_a') {
        // Monday, Wednesday, Friday only
        isValid = (day === 1 || day === 3 || day === 5);
      } else if (frequency === 'package_b') {
        // Tuesday, Thursday, Saturday only
        isValid = (day === 2 || day === 4 || day === 6);
      } else if (frequency === 'weekdays') {
        isValid = (day >= 1 && day <= 5);
      } else if (frequency === 'daily' || frequency === 'demo' || frequency === 'alternate') {
        isValid = true;
      } else {
        isValid = true;
      }

      if (isValid) {
        result.push({
          session_number: result.length + 1,
          session_date: formatDateSafe(current),
          session_time: defaultTime || '07:00 AM – 08:00 AM',
          status: 'upcoming',
        });
      }

      if (frequency === 'alternate') {
        current.setDate(current.getDate() + 2);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }
    return result;
  };

  const handleSessionChange = (index, field, value) => {
    setSessionsList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const getNextDateForPattern = (lastDateStr, pattern) => {
    let cand = parseDateSafe(lastDateStr);
    cand.setDate(cand.getDate() + 1);
    let safety = 0;
    while (safety < 14) {
      safety++;
      const d = cand.getDay();
      if (pattern === 'package_a') {
        if (d === 1 || d === 3 || d === 5) break;
      } else if (pattern === 'package_b') {
        if (d === 2 || d === 4 || d === 6) break;
      } else if (pattern === 'weekdays') {
        if (d >= 1 && d <= 5) break;
      } else if (pattern === 'alternate') {
        cand.setDate(cand.getDate() + 1);
        break;
      } else {
        break;
      }
      cand.setDate(cand.getDate() + 1);
    }
    return formatDateSafe(cand);
  };

  const handleAddSessionRow = () => {
    setSessionsList((prev) => {
      const nextNum = prev.length + 1;
      let nextDate = scheduleStartDate;
      if (prev.length > 0 && prev[prev.length - 1].session_date) {
        nextDate = getNextDateForPattern(prev[prev.length - 1].session_date, scheduleFrequency);
      }
      return [
        ...prev,
        {
          session_number: nextNum,
          session_date: nextDate,
          session_time: scheduleDefaultTime,
          status: 'upcoming',
        },
      ];
    });
    setNumberOfSessions((prev) => (parseInt(prev, 10) || 0) + 1);
  };

  const handleRemoveSessionRow = (index) => {
    setSessionsList((prev) => {
      const updated = prev.filter((_, i) => i !== index).map((s, i) => ({
        ...s,
        session_number: i + 1,
      }));
      setNumberOfSessions(updated.length);
      return updated;
    });
  };

  const handleAutoFillSchedule = () => {
    const count = parseInt(numberOfSessions, 10) || 1;
    const generated = generateSessions(count, scheduleStartDate, scheduleDefaultTime, scheduleFrequency);
    setSessionsList(generated);
  };

  const handleSelectPackage = (pkg, customCount) => {
    setActivePackage(pkg);
    if (pkg === 'demo') {
      setNumberOfSessions(1);
      setScheduleFrequency('demo');
      const generated = generateSessions(1, scheduleStartDate, scheduleDefaultTime, 'demo');
      setSessionsList(generated);
    } else if (pkg === 'package_a') {
      setScheduleFrequency('package_a');
      const count = customCount || (Number(numberOfSessions) === 24 ? 24 : 12);
      setNumberOfSessions(count);
      const generated = generateSessions(count, scheduleStartDate, scheduleDefaultTime, 'package_a');
      setSessionsList(generated);
    } else if (pkg === 'package_b') {
      setScheduleFrequency('package_b');
      const count = customCount || (Number(numberOfSessions) === 24 ? 24 : 12);
      setNumberOfSessions(count);
      const generated = generateSessions(count, scheduleStartDate, scheduleDefaultTime, 'package_b');
      setSessionsList(generated);
    }
  };

  const handleAssignOpen = async (user) => {
    setSelectedUserForAssign(user);
    setSelectedTrainerId(user.assigned_trainer_id || '');
    
    // Default to 1 (Demo session) unless user already has an existing multi-session package
    const existingCount = (user.total_sessions && user.total_sessions > 1) ? user.total_sessions : 1;
    const initialPkg = existingCount === 1 ? 'demo' : 'package_a';
    setActivePackage(initialPkg);
    setScheduleFrequency(initialPkg === 'demo' ? 'demo' : 'package_a');
    setNumberOfSessions(existingCount);
    setAssignError('');
    setAssignSuccess('');

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tmrwStr = formatDateSafe(tomorrow);
    setScheduleStartDate(tmrwStr);

    setAssignDialogOpen(true);

    try {
      const res = await api.get('/trainers');
      if (res.data?.data) {
        setTrainersList(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching trainers for assignment', err);
    }

    try {
      const sRes = await api.get(`/users/${user.id}/sessions`);
      if (sRes.data?.data && sRes.data.data.length > 0) {
        setSessionsList(sRes.data.data);
        setNumberOfSessions(sRes.data.data.length);
        if (sRes.data.data.length === 1) {
          setActivePackage('demo');
          setScheduleFrequency('demo');
        } else {
          // Detect whether sessions are Mon/Wed/Fri (Package A) or Tue/Thu/Sat (Package B)
          const firstDate = sRes.data.data[0]?.session_date;
          if (firstDate) {
            const dayNum = parseDateSafe(firstDate).getDay();
            if (dayNum === 2 || dayNum === 4 || dayNum === 6) {
              setActivePackage('package_b');
              setScheduleFrequency('package_b');
            } else {
              setActivePackage('package_a');
              setScheduleFrequency('package_a');
            }
          }
        }
      } else {
        const defaultList = generateSessions(
          existingCount,
          tmrwStr,
          '07:00 AM – 08:00 AM',
          initialPkg === 'demo' ? 'demo' : 'package_a'
        );
        setSessionsList(defaultList);
      }
    } catch (err) {
      const defaultList = generateSessions(
        existingCount,
        tmrwStr,
        '07:00 AM – 08:00 AM',
        initialPkg === 'demo' ? 'demo' : 'package_a'
      );
      setSessionsList(defaultList);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedUserForAssign) return;
    setAssigningLoading(true);
    setAssignError('');
    setAssignSuccess('');
    try {
      if (!selectedTrainerId) {
        // Unassign trainer
        const res = await api.post(`/users/${selectedUserForAssign.id}/unassign-trainer`);
        setAssignSuccess(res.data?.message || 'Trainer unassigned successfully!');
      } else {
        // Assign trainer with scheduled sessions (dates and times)
        const res = await api.post(`/users/${selectedUserForAssign.id}/assign-trainer`, {
          trainer_id: selectedTrainerId,
          assigned_by: 'admin',
          number_of_sessions: sessionsList.length || numberOfSessions,
          sessions: sessionsList,
        });
        setAssignSuccess(res.data?.message || 'Trainer and session schedule saved successfully!');
      }
      fetchUsers();
      setTimeout(() => {
        setAssignDialogOpen(false);
      }, 1200);
    } catch (err) {
      setAssignError(err.response?.data?.error || err.message || 'Failed to update assignment');
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleViewOpen = async (user) => {
    setViewUser(user);
    setViewOpen(true);
    setViewSessions([]);
    try {
      const res = await api.get(`/users/${user.id}/sessions`);
      if (res.data?.data) {
        setViewSessions(res.data.data);
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchUsers();
    fetchStates();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (filterState) {
      fetchDistrictsForFilter(filterState);
    } else {
      setFilterDistricts([]);
      setFilterDistrict('');
    }
  }, [filterState]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/users-list');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/locations/states');
      setStates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setDistricts(response.data.data || []);
      setCities([]); // Reset cities when district changes
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchDistrictsForFilter = async (stateId) => {
    if (!stateId) {
      setFilterDistricts([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setFilterDistricts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching districts for filter:', error);
    }
  };

  const fetchCities = async (districtId) => {
    if (!districtId) {
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/districts/${districtId}/cities`);
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.data || []);
      // Flatten all subcategories
      const allSubcategories = [];
      response.data.data.forEach(cat => {
        if (cat.subcategories) {
          cat.subcategories.forEach(sub => {
            allSubcategories.push({ ...sub, category_name: cat.name });
          });
        }
      });
      setSubcategories(allSubcategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getSubcategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    return subcategories.filter(sub => sub.category_id === parseInt(categoryId));
  };

  const handleOpen = async (user = null) => {
    if (user) {
      setEditing(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        gender: user.gender || '',
        source_website: user.source_website || '',
        state_id: user.state_id || '',
        district_id: user.district_id || '',
        city_id: user.city_id || '',
        area: user.area || '',
        pincode: user.pincode || '',
        start_plan: user.start_plan || '',
        available_days: user.available_days || '',
        service_type: user.service_type || 'home_services',
        category_id: user.category_id || '',
        subcategory_ids: user.subcategories ? user.subcategories.map(s => s.id) : [],
      });
      // Fetch districts and cities if IDs are available
      if (user.state_id) {
        await fetchDistricts(user.state_id);
      }
      if (user.district_id) {
        await fetchCities(user.district_id);
      }
    } else {
      setEditing(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        gender: '',
        source_website: '',
        state_id: '',
        district_id: '',
        city_id: '',
        area: '',
        pincode: '',
        start_plan: '',
        available_days: '',
        service_type: 'home_services',
        category_id: '',
        subcategory_ids: [],
      });
      setDistricts([]);
      setCities([]);
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      gender: '',
      source_website: '',
      state_id: '',
      district_id: '',
      city_id: '',
      area: '',
      pincode: '',
      start_plan: '',
      available_days: '',
      service_type: 'home_services',
      category_id: '',
      subcategory_ids: [],
    });
    setDistricts([]);
    setCities([]);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const dataToSend = {
        ...formData,
        subcategory_ids: formData.subcategory_ids && formData.subcategory_ids.length > 0
          ? formData.subcategory_ids
          : null
      };
      if (editing) {
        await api.put(`/users/${editing.id}`, dataToSend);
      } else {
        await api.post('/users', dataToSend);
      }
      handleClose();
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving user');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewUser(null);
    setViewSessions([]);
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilterState('');
    setFilterDistrict('');
    setFilterArea('');
    setFilterJoinedMonth('');
    setFilterGender('');
    setFilterEmail('');
    setFilterPhone('');
    setFilterDistricts([]);
  };

  // Distinct months when users joined the app
  const availableJoinedMonths = useMemo(() => {
    const monthMap = new Map();
    users.forEach((user) => {
      if (!user.created_at) return;
      const d = new Date(user.created_at);
      if (isNaN(d.getTime())) return;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const key = `${yyyy}-${mm}`;
      const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (!monthMap.has(key)) {
        monthMap.set(key, { key, label, count: 0 });
      }
      monthMap.get(key).count += 1;
    });
    return Array.from(monthMap.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [users]);

  // Pre-defined areas for currently selected filter district
  const availableFilterAreas = useMemo(() => {
    return getAreasForDistrict(filterDistrict, filterDistricts.length > 0 ? filterDistricts : districts, users);
  }, [filterDistrict, filterDistricts, districts, users]);

  // Pre-defined areas for user add/edit dialog based on selected district
  const availableFormAreas = useMemo(() => {
    return getAreasForDistrict(formData.district_id, districts, users);
  }, [formData.district_id, districts, users]);

  const usersMatchingOtherFilters = useMemo(() => {
    let filtered = [...users];

    if (filterState) {
      filtered = filtered.filter(user => String(user.state_id) === String(filterState));
    }

    if (filterDistrict) {
      filtered = filtered.filter(user => String(user.district_id) === String(filterDistrict));
    }

    if (filterArea) {
      const areaLower = filterArea.toLowerCase().trim();
      filtered = filtered.filter(user => {
        const directArea = String(user.area || '').toLowerCase();
        const mongoArea = String(user.mongo_location?.area || '').toLowerCase();
        const displayLoc = String(user.mongo_location?.display_location || '').toLowerCase();
        return directArea.includes(areaLower) || mongoArea.includes(areaLower) || displayLoc.includes(areaLower);
      });
    }

    if (filterJoinedMonth) {
      filtered = filtered.filter(user => {
        if (!user.created_at) return false;
        const d = new Date(user.created_at);
        if (isNaN(d.getTime())) return false;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${yyyy}-${mm}` === filterJoinedMonth;
      });
    }

    if (filterEmail) {
      const emailSearch = filterEmail.toLowerCase().trim();
      filtered = filtered.filter(user => String(user.email || '').toLowerCase().includes(emailSearch));
    }

    if (filterPhone) {
      const phoneSearch = filterPhone.toLowerCase().trim();
      filtered = filtered.filter(user => String(user.phone || '').toLowerCase().includes(phoneSearch));
    }

    return filtered;
  }, [users, filterState, filterDistrict, filterArea, filterJoinedMonth, filterEmail, filterPhone]);

  const filteredUsers = useMemo(() => {
    if (!filterGender) return usersMatchingOtherFilters;

    const selectedGender = filterGender.trim().toLowerCase();
    return usersMatchingOtherFilters.filter(
      user => String(user.gender || '').trim().toLowerCase() === selectedGender
    );
  }, [usersMatchingOtherFilters, filterGender]);

  const genderCounts = useMemo(() => {
    return usersMatchingOtherFilters.reduce((acc, user) => {
      const key = String(user.gender || '').trim().toLowerCase();
      if (key) {
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {});
  }, [usersMatchingOtherFilters]);

  // Export users to Excel
  const handleExportExcel = async () => {
    // Fetch all districts for all states that are in the filtered users
    const stateIds = [...new Set(filteredUsers.map(u => u.state_id).filter(Boolean))];
    const districtsMap = new Map();
    
    // Fetch districts for each state
    for (const stateId of stateIds) {
      try {
        const response = await api.get(`/locations/states/${stateId}/districts`);
        const districtList = response.data.data || [];
        districtList.forEach(district => {
          districtsMap.set(district.id, district.name);
        });
      } catch (error) {
        console.error(`Error fetching districts for state ${stateId}:`, error);
      }
    }

    const exportData = filteredUsers.map(user => ({
      'ID': user.id,
      'Enquiry Date & Time': formatTimestamp(user.created_at),
      'Name': user.name || '',
      'Email': user.email || '',
      'Phone': user.phone || '',
      'Gender': user.gender || '',
      'State': states.find(s => s.id === user.state_id)?.name || '',
      'District': districtsMap.get(user.district_id) || '',
      'Area': user.area || '',
      'Pincode': user.pincode || '',
      'Service Type': user.service_type || '',
      'Start Plan': user.start_plan || '',
      'Available Days': user.available_days || '',
      'Source Website': user.source_website || '',
      'Category': categories.find(c => c.id === user.category_id)?.name || '',
      'Subcategories': user.subcategories?.map(s => s.name).join(', ') || '',
    }));

    if (exportData.length === 0) {
      alert('No data to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    
    // Auto-size columns
    const maxWidth = 50;
    const wscols = Object.keys(exportData[0] || {}).map(() => ({ wch: maxWidth }));
    ws['!cols'] = wscols;

    XLSX.writeFile(wb, `users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Handle Excel file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadError('');
    setUploadSuccess('');

    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
      });

      // Validate and process the data
      const usersToCreate = [];
      const errors = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNum = i + 2; // +2 because Excel is 1-indexed and has header

        // Required fields validation
        if (!row.Name || !row.Email) {
          errors.push(`Row ${rowNum}: Name and Email are required`);
          continue;
        }

        // Find state_id if state name is provided
        let stateId = null;
        if (row.State) {
          const state = states.find(s => 
            s.name.toLowerCase() === row.State.toLowerCase().trim()
          );
          if (state) {
            stateId = state.id;
          } else {
            errors.push(`Row ${rowNum}: State "${row.State}" not found`);
          }
        }

        // Find district_id if district name is provided
        let districtId = null;
        if (row.District && stateId) {
          // First check filterDistricts (if state matches current filter)
          let district = filterDistricts.find(d => 
            d.name.toLowerCase() === row.District.toLowerCase().trim()
          );
          
          if (!district) {
            // Try fetching districts for the state
            try {
              const distResponse = await api.get(`/locations/states/${stateId}/districts`);
              const districtList = distResponse.data.data || [];
              district = districtList.find(d => 
                d.name.toLowerCase() === row.District.toLowerCase().trim()
              );
            } catch (error) {
              errors.push(`Row ${rowNum}: Error fetching districts for state`);
            }
          }
          
          if (district) {
            districtId = district.id;
          } else {
            errors.push(`Row ${rowNum}: District "${row.District}" not found`);
          }
        }

        // Find category_id if category name is provided
        let categoryId = null;
        if (row.Category) {
          const category = categories.find(c => 
            c.name.toLowerCase() === row.Category.toLowerCase().trim()
          );
          if (category) {
            categoryId = category.id;
          } else {
            errors.push(`Row ${rowNum}: Category "${row.Category}" not found`);
          }
        }

        // Process subcategories
        let subcategoryIds = [];
        if (row.Subcategories && categoryId) {
          const subcatNames = row.Subcategories.split(',').map(s => s.trim());
          const categorySubcats = subcategories.filter(s => s.category_id === categoryId);
          subcatNames.forEach(subcatName => {
            const subcat = categorySubcats.find(s => 
              s.name.toLowerCase() === subcatName.toLowerCase()
            );
            if (subcat) {
              subcategoryIds.push(subcat.id);
            }
          });
        }

        usersToCreate.push({
          name: row.Name.trim(),
          email: row.Email.trim(),
          phone: row.Phone?.toString().trim() || '',
          gender: row.Gender?.toLowerCase() || '',
          state_id: stateId || '',
          district_id: districtId || '',
          area: row.Area?.trim() || '',
          pincode: row.Pincode?.toString().trim() || '',
          service_type: row['Service Type']?.toLowerCase() === 'online_services' ? 'online_services' : 'home_services',
          start_plan: row['Start Plan']?.toLowerCase() || '',
          available_days: row['Available Days']?.toLowerCase() || '',
          source_website: row['Source Website']?.trim() || '',
          category_id: categoryId || '',
          subcategory_ids: subcategoryIds.length > 0 ? subcategoryIds : null,
        });
      }

      if (usersToCreate.length > 0) {
        // Check for duplicates within the Excel file
        const emailSet = new Set();
        const duplicateEmailsInFile = [];
        const uniqueUsersToCreate = [];

        usersToCreate.forEach((userData, index) => {
          const emailLower = userData.email.toLowerCase();
          if (emailSet.has(emailLower)) {
            duplicateEmailsInFile.push(`Row ${index + 2}: Email "${userData.email}" is duplicated in the file`);
          } else {
            emailSet.add(emailLower);
            uniqueUsersToCreate.push(userData);
          }
        });

        if (duplicateEmailsInFile.length > 0) {
          errors.push(...duplicateEmailsInFile);
        }

        // Check for duplicates against existing users
        const existingEmails = new Set(users.map(u => u.email?.toLowerCase()).filter(Boolean));
        const duplicateEmailsInDB = [];
        const finalUsersToCreate = [];

        uniqueUsersToCreate.forEach((userData, index) => {
          const emailLower = userData.email.toLowerCase();
          if (existingEmails.has(emailLower)) {
            duplicateEmailsInDB.push(`Row ${index + 2}: Email "${userData.email}" already exists in the system`);
          } else {
            finalUsersToCreate.push(userData);
            existingEmails.add(emailLower); // Track to avoid duplicates in the same batch
          }
        });

        if (duplicateEmailsInDB.length > 0) {
          errors.push(...duplicateEmailsInDB);
        }

        // Display all validation errors at once
        if (errors.length > 0) {
          setUploadError(`Validation errors:\n${errors.join('\n')}`);
        }

        // Create users one by one (only non-duplicate users)
        let successCount = 0;
        let failCount = 0;
        const failErrors = [];

        for (const userData of finalUsersToCreate) {
          try {
            await api.post('/users', userData);
            successCount++;
          } catch (error) {
            failCount++;
            const errorMsg = error.response?.data?.error || error.message;
            // Check if it's a duplicate error from backend (additional safety check)
            if (errorMsg.toLowerCase().includes('duplicate') || errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('email')) {
              failErrors.push(`${userData.email}: Email already exists`);
            } else {
              failErrors.push(`${userData.email}: ${errorMsg}`);
            }
          }
        }

        // Consolidate all error messages
        const allErrors = [];
        if (errors.length > 0) {
          allErrors.push(`Validation errors:\n${errors.join('\n')}`);
        }
        if (failCount > 0) {
          allErrors.push(`\nFailed to import ${failCount} user(s):\n${failErrors.join('\n')}`);
        }

        if (allErrors.length > 0) {
          setUploadError(allErrors.join('\n\n'));
        }

        if (successCount > 0) {
          setUploadSuccess(`Successfully imported ${successCount} user(s)`);
          fetchUsers();
        }
      }

      // Reset file input
      event.target.value = '';
    } catch (error) {
      setUploadError(`Error reading file: ${error.message}`);
      event.target.value = '';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            startIcon={<UploadFileIcon />} 
            onClick={() => setUploadDialogOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleExportExcel}
            disabled={filteredUsers.length === 0}
          >
            Download Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add User
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="body1">
          Showing <strong>{filteredUsers.length}</strong> user(s) out of <strong>{users.length}</strong>
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={!filterState && !filterDistrict && !filterArea && !filterJoinedMonth && !filterGender && !filterEmail && !filterPhone}
          >
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setFilterDistrict('');
                  setFilterArea('');
                }}
                label="State"
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name} ({users.filter(u => u.state_id === state.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>District</InputLabel>
              <Select
                value={filterDistrict}
                onChange={(e) => {
                  setFilterDistrict(e.target.value);
                  setFilterArea('');
                }}
                label="District"
                disabled={!filterState}
              >
                <MenuItem value="">All Districts</MenuItem>
                {filterDistricts.map((district) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name} ({users.filter(u => u.district_id === district.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              options={availableFilterAreas}
              value={filterArea}
              onChange={(event, newValue) => {
                setFilterArea(newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                setFilterArea(newInputValue || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Area"
                  placeholder={
                    filterDistrict
                      ? `Select area (${availableFilterAreas.length} in district)`
                      : "Select or type area..."
                  }
                  helperText={
                    filterDistrict
                      ? `${availableFilterAreas.length} area(s) listed`
                      : 'Select district to refine areas'
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Joined Month</InputLabel>
              <Select
                value={filterJoinedMonth}
                onChange={(e) => setFilterJoinedMonth(e.target.value)}
                label="Joined Month"
              >
                <MenuItem value="">All Months ({users.length})</MenuItem>
                {availableJoinedMonths.map((m) => (
                  <MenuItem key={m.key} value={m.key}>
                    {m.label} ({m.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                label="Gender"
              >
                <MenuItem value="">All Genders ({usersMatchingOtherFilters.length})</MenuItem>
                <MenuItem value="male">Male ({genderCounts.male || 0})</MenuItem>
                <MenuItem value="female">Female ({genderCounts.female || 0})</MenuItem>
                <MenuItem value="other">Other ({genderCounts.other || 0})</MenuItem>
                <MenuItem value="others">Others ({genderCounts.others || 0})</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search Email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Enter email..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search Mobile"
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
              placeholder="Enter mobile..."
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        data={filteredUsers}
        loading={loading}
        searchFields={['name', 'email', 'phone']}
        emptyMessage="No users found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'created_at', label: 'Enquiry Date & Time' },
          { field: 'name', label: 'Name' },
          { field: 'email', label: 'Email' },
          { field: 'phone', label: 'Phone' },
          { field: 'service_type', label: 'Service Type' },
          { field: 'location', label: 'Location' },
          { field: 'assigned_trainer', label: 'Assigned Trainer' },
          { field: 'trainer_interactions', label: 'Trainer Interactions' },
          { field: 'source_website', label: 'Source Website' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(user) => (
          <TableRow key={user.id}>
            <TableCell>{user.id}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2">
                {formatTimestamp(user.created_at)}
              </Typography>
            </TableCell>
            <TableCell>{user.name}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phone || '-'}</TableCell>
            <TableCell>{user.service_type || 'home_services'}</TableCell>
            <TableCell sx={{ minWidth: 170 }}>
              {user.mongo_location && user.mongo_location.has_location ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: '#e53935' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user.mongo_location.display_location || [user.area, user.district].filter(Boolean).join(', ') || '-'}
                    </Typography>
                  </Box>
                  {user.mongo_location.google_maps_url && (
                    <Link
                      href={user.mongo_location.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: '0.72rem',
                        color: 'primary.main',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.3,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 12 }} />
                      View Map ({Number(user.mongo_location.latitude).toFixed(4)}, {Number(user.mongo_location.longitude).toFixed(4)})
                    </Link>
                  )}
                  {user.mongo_location.source?.startsWith('mongodb') && (
                    <Chip
                      label="MongoDB"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 18 }}
                    />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {[user.area, user.district, user.pincode].filter(Boolean).join(', ') || '-'}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              {user.assigned_trainer_name ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                  <Chip
                    label={user.assigned_trainer_name}
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${user.completed_sessions || 0}/${user.total_sessions || 1} Sessions`}
                    size="small"
                    color={Number(user.completed_sessions || 0) >= Number(user.total_sessions || 1) ? 'default' : 'primary'}
                    variant={Number(user.completed_sessions || 0) >= Number(user.total_sessions || 1) ? 'outlined' : 'filled'}
                    sx={{ fontSize: '0.70rem', height: 20 }}
                  />
                </Box>
              ) : (
                <Chip
                  label="Unassigned"
                  size="small"
                  variant="outlined"
                  color="default"
                />
              )}
            </TableCell>
            <TableCell>
              {user.trainer_interactions ? (
                <Box>
                  <Typography variant="body2">
                    Views: {user.trainer_interactions.view_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Responses: {user.trainer_interactions.respond_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {user.trainer_interactions.total_count || 0}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">0</Typography>
              )}
            </TableCell>
            <TableCell>{user.source_website || '-'}</TableCell>
            <TableCell>
              <Tooltip title="Assign Trainer">
                <IconButton size="small" color="primary" onClick={() => handleAssignOpen(user)}>
                  <AssignmentIndIcon />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => handleViewOpen(user)}>
                <VisibilityIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleOpen(user)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(user.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      {/* Bulk Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        setUploadDialogOpen(false);
        setUploadError('');
        setUploadSuccess('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Users</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadSuccess}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload an Excel file (.xlsx) with the following columns:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><strong>Name</strong> (required)</li>
            <li><strong>Email</strong> (required)</li>
            <li>Phone</li>
            <li>Gender</li>
            <li>State</li>
            <li>District</li>
            <li>Area</li>
            <li>Pincode</li>
            <li>Service Type</li>
            <li>Start Plan</li>
            <li>Available Days</li>
            <li>Source Website</li>
            <li>Category</li>
            <li>Subcategories (comma-separated)</li>
          </Box>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-upload-input"
          />
          <label htmlFor="excel-upload-input">
            <Button variant="contained" component="span" fullWidth>
              Choose Excel File
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadError('');
            setUploadSuccess('');
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender || ''}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              label="Gender"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Pincode"
            value={formData.pincode || ''}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
            margin="normal"
            inputProps={{ maxLength: 10 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>When do you plan to start Personal trainer?</InputLabel>
            <Select
              value={formData.start_plan || ''}
              onChange={(e) => setFormData({ ...formData, start_plan: e.target.value })}
              label="When do you plan to start Personal trainer?"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="immediately">Immediately</MenuItem>
              <MenuItem value="within_a_month">Within a Month</MenuItem>
              <MenuItem value="not_sure">Not Sure, Just want to look at options</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>What Days are you generally available to take classes?</InputLabel>
            <Select
              value={formData.available_days || ''}
              onChange={(e) => setFormData({ ...formData, available_days: e.target.value })}
              label="What Days are you generally available to take classes?"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="any_day">Any day</MenuItem>
              <MenuItem value="weekday">Weekday</MenuItem>
              <MenuItem value="weekend">Weekend</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>State</InputLabel>
            <Select
              value={formData.state_id || ''}
              onChange={async (e) => {
                const stateId = e.target.value;
                setFormData({
                  ...formData,
                  state_id: stateId,
                  district_id: '',
                  city_id: ''
                });
                await fetchDistricts(stateId);
              }}
              label="State"
            >
              <MenuItem value="">None</MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>District</InputLabel>
            <Select
              value={formData.district_id || ''}
              onChange={async (e) => {
                const districtId = e.target.value;
                setFormData({
                  ...formData,
                  district_id: districtId,
                  city_id: '',
                  area: ''
                });
                await fetchCities(districtId);
              }}
              label="District"
              disabled={!formData.state_id}
            >
              <MenuItem value="">None</MenuItem>
              {districts.map((district) => (
                <MenuItem key={district.id} value={district.id}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={availableFormAreas}
            value={formData.area || ''}
            onChange={(event, newValue) => {
              setFormData({ ...formData, area: newValue || '' });
            }}
            onInputChange={(event, newInputValue) => {
              setFormData({ ...formData, area: newInputValue || '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Area"
                margin="normal"
                placeholder={
                  formData.district_id
                    ? `Select or enter area (${availableFormAreas.length} available)`
                    : 'Select district first, or type area...'
                }
                helperText={
                  formData.district_id
                    ? `${availableFormAreas.length} pre-defined area(s) for this district`
                    : 'Select State & District above to pick from pre-defined areas'
                }
              />
            )}
          />
          {/* <FormControl fullWidth margin="normal">
            <InputLabel>City</InputLabel>
            <Select
              value={formData.city_id || ''}
              onChange={(e) => {
                setFormData({ ...formData, city_id: e.target.value });
              }}
              label="City"
              disabled={!formData.district_id}
            >
              <MenuItem value="">None</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          {/* <TextField
            fullWidth
            label="District (Legacy)"
            value={formData.district || ''}
            onChange={(e) => setFormData({ ...formData, district: e.target.value })}
            margin="normal"
            helperText="Optional: Use if district dropdown is not available"
          />
          <TextField
            fullWidth
            label="City (Legacy)"
            value={formData.city || ''}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            margin="normal"
            helperText="Optional: Use if city dropdown is not available"
          /> */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Service Type</InputLabel>
            <Select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              label="Service Type"
            >
              <MenuItem value="home_services">Home Services</MenuItem>
              <MenuItem value="online_services">Online Services</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Professional Specializations</InputLabel>
            <Select
              value={formData.category_id || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  category_id: e.target.value,
                  subcategory_ids: [] // Reset subcategories when category changes
                });
              }}
              label="CaProfessional Specializationstegory"
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Providing Services</InputLabel>
            <Select
              multiple
              value={formData.subcategory_ids || []}
              onChange={(e) => setFormData({ ...formData, subcategory_ids: e.target.value })}
              input={<OutlinedInput label="Providing Services" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const subcat = subcategories.find(s => s.id === value);
                    return subcat ? (
                      <Chip key={value} label={subcat.name} size="small" />
                    ) : null;
                  })}
                </Box>
              )}
              disabled={!formData.category_id}
            >
              {getSubcategoriesForCategory(formData.category_id).map((subcat) => (
                <MenuItem key={subcat.id} value={subcat.id}>
                  <Checkbox checked={formData.subcategory_ids?.indexOf(subcat.id) > -1} />
                  <ListItemText primary={subcat.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <TextField
            fullWidth
            label="Source Website"
            value={formData.source_website || ''}
            onChange={(e) => setFormData({ ...formData, source_website: e.target.value })}
            margin="normal"
            readOnly
          /> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {viewUser && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewUser.id}</Typography>
              <Typography variant="body2"><strong>Enquiry Date & Time:</strong> {formatTimestamp(viewUser.created_at)}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {viewUser.name || '-'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {viewUser.email || '-'}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> {viewUser.phone || '-'}</Typography>
              <Typography variant="body2"><strong>Gender:</strong> {viewUser.gender || '-'}</Typography>
              <Typography variant="body2"><strong>Service Type:</strong> {viewUser.service_type || '-'}</Typography>
              <Typography variant="body2"><strong>Assigned Trainer:</strong> {viewUser.assigned_trainer_name || 'Unassigned'}</Typography>
              <Typography variant="body2"><strong>Source Website:</strong> {viewUser.source_website || '-'}</Typography>
              <Typography variant="body2"><strong>Area:</strong> {viewUser.mongo_location?.area || viewUser.area || '-'}</Typography>
              <Typography variant="body2"><strong>District / State:</strong> {[viewUser.mongo_location?.district || viewUser.district, viewUser.mongo_location?.state || viewUser.state].filter(Boolean).join(', ') || '-'}</Typography>
              <Typography variant="body2"><strong>Pincode:</strong> {viewUser.mongo_location?.pincode || viewUser.pincode || '-'}</Typography>
              {viewUser.mongo_location?.address && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Address (MongoDB):</strong> {viewUser.mongo_location.address}
                </Typography>
              )}
              {viewUser.mongo_location?.latitude && (
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Coordinates:</strong> {Number(viewUser.mongo_location.latitude).toFixed(5)}, {Number(viewUser.mongo_location.longitude).toFixed(5)}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<OpenInNewIcon />}
                    href={viewUser.mongo_location.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: 'none', py: 0.2, px: 1, fontSize: '0.75rem' }}
                  >
                    Open in Maps
                  </Button>
                </Box>
              )}
              <Typography variant="body2"><strong>Start Plan:</strong> {viewUser.start_plan || '-'}</Typography>
              <Typography variant="body2"><strong>Available Days:</strong> {viewUser.available_days || '-'}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Subcategories:</strong></Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {viewUser.subcategories?.length
                  ? viewUser.subcategories.map((s) => <Chip key={s.id} label={s.name} size="small" />)
                  : <Typography variant="body2">-</Typography>}
              </Box>

              {viewSessions && viewSessions.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Scheduled Sessions ({viewSessions.length})
                  </Typography>
                  <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                    {viewSessions.map((s) => (
                      <Box
                        key={s.id || s.session_number}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1,
                          px: 1.5,
                          borderBottom: '1px solid #f1f5f9',
                          bgcolor: s.status === 'in_progress' ? '#f0f9ff' : 'transparent',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Session #{s.session_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.session_date} • {s.session_time}
                        </Typography>
                        <Chip
                          label={s.status}
                          size="small"
                          color={s.status === 'completed' ? 'success' : (s.status === 'in_progress' ? 'info' : 'default')}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 22 }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Trainer Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIndIcon color="primary" />
          Assign Trainer & Schedule Client Sessions
        </DialogTitle>
        <DialogContent dividers>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {assignError}
            </Alert>
          )}
          {assignSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {assignSuccess}
            </Alert>
          )}
          {selectedUserForAssign && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {/* Client Summary Box */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                  CLIENT DETAILS
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Name:</strong> {selectedUserForAssign.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {selectedUserForAssign.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Service:</strong> {selectedUserForAssign.service_type || 'Personal Training'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Location:</strong> {selectedUserForAssign.mongo_location?.display_location || [selectedUserForAssign.area, selectedUserForAssign.city, selectedUserForAssign.pincode].filter(Boolean).join(', ') || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      <strong>Current Trainer:</strong> {selectedUserForAssign.assigned_trainer_name || 'None'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Progress:</strong> {selectedUserForAssign.completed_sessions || 0}/{selectedUserForAssign.total_sessions || 1} sessions completed
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Select Trainer Mode Toggle & Autocomplete (Phone / Name) */}
              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                    Select Trainer:
                  </Typography>
                  <ToggleButtonGroup
                    value={trainerSelectMode}
                    exclusive
                    size="small"
                    onChange={(_, val) => {
                      if (val) setTrainerSelectMode(val);
                    }}
                    sx={{ bgcolor: '#f1f5f9' }}
                  >
                    <ToggleButton
                      value="phone"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        '&.Mui-selected': {
                          bgcolor: '#e0f2fe',
                          color: '#0284c7',
                          borderColor: '#38bdf8',
                        },
                      }}
                    >
                      <PhoneIcon sx={{ mr: 0.5, fontSize: 16 }} /> Select by Phone Number
                    </ToggleButton>
                    <ToggleButton
                      value="name"
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        '&.Mui-selected': {
                          bgcolor: '#e0f2fe',
                          color: '#0284c7',
                          borderColor: '#38bdf8',
                        },
                      }}
                    >
                      <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} /> Select by Name
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Autocomplete
                  options={trainersList}
                  value={selectedTrainerObj}
                  onChange={(_, newValue) => {
                    setSelectedTrainerId(newValue ? newValue.id : '');
                  }}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  getOptionLabel={(option) => {
                    if (trainerSelectMode === 'phone') {
                      return option.phone
                        ? `${option.phone} — ${option.name || 'Unnamed'}`
                        : `${option.name || 'Unnamed'} (No phone)`;
                    }
                    return `${option.name || 'Unnamed'}${option.phone ? ` (${option.phone})` : ''}`;
                  }}
                  filterOptions={(options, state) => {
                    const input = state.inputValue.toLowerCase().trim();
                    if (!input) return options;
                    return options.filter((t) => {
                      const phone = (t.phone || '').toLowerCase();
                      const name = (t.name || '').toLowerCase();
                      const email = (t.email || '').toLowerCase();
                      return phone.includes(input) || name.includes(input) || email.includes(input);
                    });
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, width: '100%' }}>
                        {trainerSelectMode === 'phone' ? (
                          <PhoneIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                        ) : (
                          <PersonIcon sx={{ color: '#64748b', fontSize: 20 }} />
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {trainerSelectMode === 'phone'
                              ? (option.phone || 'No phone')
                              : (option.name || 'Unnamed')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {trainerSelectMode === 'phone'
                              ? `${option.name || 'Unnamed'} ${option.email ? `• ${option.email}` : ''}`
                              : `Phone: ${option.phone || 'N/A'} ${option.email ? `• ${option.email}` : ''}`}
                          </Typography>
                        </Box>
                        {option.category_name && (
                          <Chip
                            label={option.category_name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        trainerSelectMode === 'phone'
                          ? "Search & Select Trainer by Phone Number"
                          : "Search & Select Trainer by Name"
                      }
                      placeholder={
                        trainerSelectMode === 'phone'
                          ? "Type mobile digits (e.g. 98401)..."
                          : "Type trainer name..."
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            {trainerSelectMode === 'phone' ? (
                              <PhoneIcon sx={{ color: 'action.active', mr: 1, ml: 0.5, fontSize: 20 }} />
                            ) : (
                              <PersonIcon sx={{ color: 'action.active', mr: 1, ml: 0.5, fontSize: 20 }} />
                            )}
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      helperText={
                        trainerSelectMode === 'phone'
                          ? "Enter any part of the trainer's phone number to find them instantly"
                          : "Enter trainer name or email to filter"
                      }
                    />
                  )}
                />

                {/* Selected Trainer Detail Banner */}
                {selectedTrainerObj && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mt: 1.5,
                      bgcolor: '#f0fdf4',
                      borderColor: '#86efac',
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: '#16a34a', width: 36, height: 36, fontSize: 16 }}>
                        {selectedTrainerObj.name ? selectedTrainerObj.name[0].toUpperCase() : 'T'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#166534' }}>
                          Selected Trainer: {selectedTrainerObj.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#15803d' }}>
                          📞 <strong>Mobile:</strong> {selectedTrainerObj.phone || 'N/A'} &nbsp;|&nbsp; ✉️ {selectedTrainerObj.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setSelectedTrainerId('')}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', height: 28 }}
                    >
                      Clear (Unassign)
                    </Button>
                  </Paper>
                )}
              </Box>

              {/* Number of Sessions & Schedule Controls */}
              {selectedTrainerId && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Package Selection Cards */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', mb: 1 }}>
                      Select Package:
                    </Typography>
                    <Grid container spacing={1.5}>
                      {/* Demo Session */}
                      <Grid item xs={12} sm={4}>
                        <Paper
                          variant="outlined"
                          onClick={() => handleSelectPackage('demo')}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderRadius: 2,
                            borderWidth: 2,
                            borderColor: activePackage === 'demo' ? '#0284c7' : '#e2e8f0',
                            bgcolor: activePackage === 'demo' ? '#f0f9ff' : '#ffffff',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': { borderColor: '#38bdf8' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: activePackage === 'demo' ? '#0369a1' : '#1e293b' }}>
                              Demo Session
                            </Typography>
                            <Chip
                              label="1 Session"
                              size="small"
                              color={activePackage === 'demo' ? 'primary' : 'default'}
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Single trial / intro session
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Package A (Mon, Wed, Fri) */}
                      <Grid item xs={12} sm={4}>
                        <Paper
                          variant="outlined"
                          onClick={() => handleSelectPackage('package_a')}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderRadius: 2,
                            borderWidth: 2,
                            borderColor: activePackage === 'package_a' ? '#16a34a' : '#e2e8f0',
                            bgcolor: activePackage === 'package_a' ? '#f0fdf4' : '#ffffff',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': { borderColor: '#4ade80' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: activePackage === 'package_a' ? '#15803d' : '#1e293b' }}>
                              Package A
                            </Typography>
                            <Chip
                              label="Mon • Wed • Fri"
                              size="small"
                              color={activePackage === 'package_a' ? 'success' : 'default'}
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: activePackage === 'package_a' ? '#166534' : 'text.secondary', fontWeight: 500 }}>
                            Monday, Wednesday, Friday
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Package B (Tue, Thu, Sat) */}
                      <Grid item xs={12} sm={4}>
                        <Paper
                          variant="outlined"
                          onClick={() => handleSelectPackage('package_b')}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderRadius: 2,
                            borderWidth: 2,
                            borderColor: activePackage === 'package_b' ? '#7c3aed' : '#e2e8f0',
                            bgcolor: activePackage === 'package_b' ? '#f5f3ff' : '#ffffff',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': { borderColor: '#a78bfa' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: activePackage === 'package_b' ? '#6d28d9' : '#1e293b' }}>
                              Package B
                            </Typography>
                            <Chip
                              label="Tue • Thu • Sat"
                              size="small"
                              color={activePackage === 'package_b' ? 'secondary' : 'default'}
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: activePackage === 'package_b' ? '#5b21b6' : 'text.secondary', fontWeight: 500 }}>
                            Tuesday, Thursday, Saturday
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Session Count Options for Package A / Package B: 12 or 24 Sessions */}
                    {activePackage !== 'demo' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                          Select Sessions:
                        </Typography>
                        {[
                          { label: '12 Sessions', count: 12 },
                          { label: '24 Sessions', count: 24 },
                        ].map((opt) => (
                          <Chip
                            key={opt.count}
                            label={opt.label}
                            clickable
                            color={Number(numberOfSessions) === opt.count ? 'primary' : 'default'}
                            variant={Number(numberOfSessions) === opt.count ? 'filled' : 'outlined'}
                            onClick={() => handleSelectPackage(activePackage, opt.count)}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              px: 1,
                              py: 0.5,
                              borderColor: Number(numberOfSessions) === opt.count ? 'primary.main' : '#cbd5e1',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Number of Sessions Custom Input */}
                  <TextField
                    fullWidth
                    label="Number of Sessions"
                    type="number"
                    value={numberOfSessions}
                    onChange={(e) => {
                      const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                      setNumberOfSessions(val);
                      setSessionsList(generateSessions(val, scheduleStartDate, scheduleDefaultTime, scheduleFrequency));
                    }}
                    inputProps={{ min: 1, max: 365 }}
                    helperText={
                      activePackage === 'demo'
                        ? 'Demo package is 1 session'
                        : `Selected: ${numberOfSessions} sessions (12 or 24 sessions)`
                    }
                  />

                  {/* Batch Schedule Auto-Fill Bar - Only shown for multi-session packages (> 1 session) */}
                  {Number(numberOfSessions) > 1 && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CalendarMonthIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Auto-Fill Session Dates & Times
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={3.5}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="First Session Date"
                            value={scheduleStartDate}
                            onChange={(e) => setScheduleStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={3.5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Time Slot"
                            value={scheduleDefaultTime}
                            onClick={() => handleOpenClockPicker('batch')}
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <IconButton
                                  size="small"
                                  edge="start"
                                  onClick={() => handleOpenClockPicker('batch')}
                                  sx={{ mr: 0.5, color: '#0284c7' }}
                                >
                                  <AccessTimeIcon fontSize="small" />
                                </IconButton>
                              ),
                              endAdornment: (
                                <Button
                                  size="small"
                                  onClick={() => handleOpenClockPicker('batch')}
                                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', minWidth: 'auto', px: 0.8 }}
                                >
                                  Clock
                                </Button>
                              ),
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& input': { cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
                            }}
                            helperText="Click to pick via clock"
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="freq-label">Pattern</InputLabel>
                            <Select
                              labelId="freq-label"
                              label="Pattern"
                              value={scheduleFrequency}
                              onChange={(e) => {
                                const newFreq = e.target.value;
                                setScheduleFrequency(newFreq);
                                if (newFreq === 'package_a') setActivePackage('package_a');
                                else if (newFreq === 'package_b') setActivePackage('package_b');
                              }}
                            >
                              <MenuItem value="package_a">Package A (Mon, Wed, Fri)</MenuItem>
                              <MenuItem value="package_b">Package B (Tue, Thu, Sat)</MenuItem>
                              <MenuItem value="alternate">Alternate Days</MenuItem>
                              <MenuItem value="daily">Daily (Every Day)</MenuItem>
                              <MenuItem value="weekdays">Weekdays Only (Mon – Fri)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={handleAutoFillSchedule}
                            sx={{ height: 40, whiteSpace: 'nowrap' }}
                          >
                            Auto-Fill
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Individual Session Date & Time Rows */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {Number(numberOfSessions) === 1
                          ? 'Demo Session Schedule (1 Session)'
                          : `Session Schedule Details (${sessionsList.length} Sessions)`}
                      </Typography>
                      {Number(numberOfSessions) > 1 && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddSessionRow}
                        >
                          Add Session
                        </Button>
                      )}
                    </Box>

                    <Box
                      sx={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        border: '1px solid #e2e8f0',
                        borderRadius: 1.5,
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        bgcolor: '#fafafa',
                      }}
                    >
                      {sessionsList.map((s, idx) => (
                        <Paper
                          key={idx}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            bgcolor: '#ffffff',
                          }}
                        >
                          <Chip
                            label={`#${s.session_number || idx + 1}${getDayLabel(s.session_date) ? ` • ${getDayLabel(s.session_date)}` : ''}`}
                            size="small"
                            color={
                              scheduleFrequency === 'package_a'
                                ? 'success'
                                : scheduleFrequency === 'package_b'
                                ? 'secondary'
                                : 'primary'
                            }
                            variant="filled"
                            sx={{ fontWeight: 700, minWidth: 70 }}
                          />

                          <TextField
                            size="small"
                            type="date"
                            label="Date"
                            value={s.session_date || ''}
                            onChange={(e) => handleSessionChange(idx, 'session_date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 150, flex: 1 }}
                          />

                          <TextField
                            size="small"
                            label="Time Slot"
                            value={s.session_time || ''}
                            onClick={() => handleOpenClockPicker(idx)}
                            placeholder="Click clock to set..."
                            InputProps={{
                              readOnly: true,
                              startAdornment: (
                                <IconButton
                                  size="small"
                                  edge="start"
                                  onClick={() => handleOpenClockPicker(idx)}
                                  sx={{ mr: 0.5, color: '#0284c7' }}
                                >
                                  <AccessTimeIcon fontSize="small" />
                                </IconButton>
                              ),
                            }}
                            sx={{
                              minWidth: 200,
                              flex: 1,
                              cursor: 'pointer',
                              '& input': { cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
                            }}
                          />

                          <Chip
                            label={s.status || 'upcoming'}
                            size="small"
                            color={s.status === 'completed' ? 'success' : (s.status === 'in_progress' ? 'info' : 'default')}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize', fontSize: '0.72rem' }}
                          />

                          {sessionsList.length > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveSessionRow(idx)}
                              title="Remove this session"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Paper>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      💡 These dates and times update directly in the Trainer app's Daily Sessions list.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigningLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignSubmit}
            variant="contained"
            disabled={assigningLoading}
          >
            {assigningLoading ? <CircularProgress size={20} /> : 'Save Assignment & Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clock-like Time Slot Picker Dialog */}
      <ClockTimeSlotPicker
        open={clockPickerOpen}
        initialValue={clockPickerValue}
        onClose={() => setClockPickerOpen(false)}
        onSelect={handleClockTimeSelect}
        title={
          clockPickerTarget === 'batch'
            ? 'Set Time Slot for All Sessions'
            : typeof clockPickerTarget === 'number'
            ? `Set Time for Session #${sessionsList[clockPickerTarget]?.session_number || clockPickerTarget + 1}`
            : 'Select Time Slot'
        }
      />
    </Box>
  );
}

export default Users;